/**
 * Nutrition V2 — the only path between the builder and the server.
 *
 * Replaces `componenets/nutrition/v2/mock/nutritionV2Store.ts`, whose own header said it was the
 * one file that would have to change when the API existed. It was, near enough: everything below
 * keeps the store's function names and argument shapes so the call sites read the same, and the
 * only real difference is that these return promises.
 *
 * Every call goes through the generated OpenAPI SDK, per progress-admin/CLAUDE.md — that is what
 * makes a client/server mismatch a compile error instead of a production bug.
 *
 * ── ids ──
 * The server's ids are numbers; the client model has used strings since it was written against a
 * mock store, and `uid` is its React key throughout. The DTOs already emit strings, so the only
 * conversion needed is on the way IN, and it lives here rather than at ten call sites.
 */

import {
    adminNutritionMenuV2ControllerListForTrainee,
    adminNutritionMenuV2ControllerGet,
    adminNutritionMenuV2ControllerCreate,
    adminNutritionMenuV2ControllerUpdate,
    adminNutritionMenuV2ControllerDelete,
    adminNutritionMenuV2ControllerDuplicate,
    adminNutritionMenuV2ControllerSetActive,
    adminNutritionLibraryV2ControllerListSavedMeals,
    adminNutritionLibraryV2ControllerCreateSavedMeal,
    adminNutritionLibraryV2ControllerUpdateSavedMeal,
    adminNutritionLibraryV2ControllerDeleteSavedMeal,
    adminNutritionLibraryV2ControllerListTemplates,
    adminNutritionLibraryV2ControllerCreateTemplate,
    adminNutritionLibraryV2ControllerGetTemplate,
    adminNutritionLibraryV2ControllerUpdateTemplate,
    adminNutritionLibraryV2ControllerDeleteTemplate,
} from "../api/generated";
import { searchFoods, createFood, deleteFood } from "./FoodService";
import type { CatalogFood } from "./catalogFood";
import type {
    MenuV2Dto,
    SaveMenuRequest,
    SavedMealV2Dto,
    MenuTemplateV2Dto,
    MenuTemplateV2Request,
} from "../api/generated/types.gen";
import type {
    CompanyFoodDraft,
    CompanyFoodV2,
    MealV2,
    MenuTemplateV2,
    MenuV2,
    SavedMealV2,
} from "../componenets/nutrition/v2/model/nutritionV2Model";
import { toServingOptions } from "../componenets/nutrition/v2/model/nutritionV2Model";

/** The generated SDK returns `{data, error}`; every endpoint here wraps its payload in HttpResponse. */
const unwrap = <T,>(result: { data?: { body?: T }; error?: unknown }, what: string): T => {
    if (result.error) {
        throw result.error;
    }
    const body = result.data?.body;
    if (body === undefined || body === null) {
        throw new Error(`${what}: empty response body`);
    }
    return body;
};

const asId = (id: string): number => Number(id);

// ── menus ───────────────────────────────────────────────────────────────────

/**
 * The server returns exactly the client's `MenuV2` shape, so the cast is a rename rather than a
 * conversion. It is a cast and not a mapper on purpose: a hand-written mapper here would silently
 * drop any field added on the server, which is the drift the generated client exists to prevent.
 */
const toMenu = (dto: MenuV2Dto): MenuV2 => dto as unknown as MenuV2;

export const listMenus = async (traineeId: string): Promise<MenuV2[]> => {
    const result = await adminNutritionMenuV2ControllerListForTrainee({
        query: { traineeId: asId(traineeId) },
    });
    return unwrap<MenuV2Dto[]>(result, "listMenus").map(toMenu);
};

export const getMenu = async (menuId: string): Promise<MenuV2> => {
    const result = await adminNutritionMenuV2ControllerGet({ path: { id: asId(menuId) } });
    return toMenu(unwrap<MenuV2Dto>(result, "getMenu"));
};

/**
 * The payload a save sends. Deliberately omits `active`, `ownedBy` and `notesDate` — the server
 * owns all three (activation has its own endpoint, the note's date follows the note's text, and a
 * trainer's menu is always coach-owned), and sending them would only invite the client to believe
 * it had set them.
 */
const toSaveRequest = (menu: MenuV2): SaveMenuRequest => ({
    name: menu.name,
    applicableDays: menu.applicableDays,
    menuType: menu.menuType,
    targets: menu.targets,
    qualityProteinPct: menu.qualityProteinPct,
    waterGoalMl: menu.waterGoalMl,
    notes: menu.notes,
    notesVisible: menu.notesVisible,
    pdfUrl: menu.pdfUrl,
    pdfName: menu.pdfName,
    calculator: menu.calculator as SaveMenuRequest["calculator"],
    meals: menu.meals as unknown as SaveMenuRequest["meals"],
    supplements: menu.supplements as unknown as SaveMenuRequest["supplements"],
});

/**
 * Create or update, chosen by whether the menu already has a server id.
 *
 * A menu the builder has not saved yet carries a locally-generated id (`menu_1_abc`), which is not
 * a number — that is the signal to POST. Returning the server's menu rather than the one passed in
 * is what lets the builder adopt real ids for every meal and item in one step instead of
 * reconciling its own uids against them.
 */
export const saveMenu = async (menu: MenuV2, templateId?: string): Promise<MenuV2> => {
    const existingId = Number(menu.id);
    if (Number.isFinite(existingId) && existingId > 0) {
        const result = await adminNutritionMenuV2ControllerUpdate({
            path: { id: existingId },
            body: toSaveRequest(menu),
        });
        return toMenu(unwrap<MenuV2Dto>(result, "saveMenu"));
    }

    const result = await adminNutritionMenuV2ControllerCreate({
        body: {
            traineeId: asId(menu.traineeId),
            templateId: templateId ? asId(templateId) : undefined,
            menu: { ...toSaveRequest(menu), active: menu.active },
        },
    });
    return toMenu(unwrap<MenuV2Dto>(result, "createMenu"));
};

/**
 * Activate / pause. Lives on the LIST rather than in the builder, because the decision is about
 * this menu relative to the trainee's others. Several may be active at once, on purpose.
 */
export const setMenuActive = async (menuId: string, active: boolean): Promise<MenuV2> => {
    const result = await adminNutritionMenuV2ControllerSetActive({
        path: { id: asId(menuId) },
        query: { active },
    });
    return toMenu(unwrap<MenuV2Dto>(result, "setMenuActive"));
};

export const deleteMenu = async (menuId: string): Promise<void> => {
    const result = await adminNutritionMenuV2ControllerDelete({ path: { id: asId(menuId) } });
    if (result.error) {
        throw result.error;
    }
};

export const duplicateMenu = async (menuId: string): Promise<MenuV2> => {
    const result = await adminNutritionMenuV2ControllerDuplicate({ path: { id: asId(menuId) } });
    return toMenu(unwrap<MenuV2Dto>(result, "duplicateMenu"));
};

// ── company saved meals ─────────────────────────────────────────────────────

const toSavedMeal = (dto: SavedMealV2Dto): SavedMealV2 => dto as unknown as SavedMealV2;

export const listSavedMeals = async (): Promise<SavedMealV2[]> => {
    const result = await adminNutritionLibraryV2ControllerListSavedMeals();
    return unwrap<SavedMealV2Dto[]>(result, "listSavedMeals").map(toSavedMeal);
};

export const saveMealToLibrary = async (
    name: string,
    meal: Pick<MealV2, "items" | "timeFrom" | "timeTo">
): Promise<SavedMealV2> => {
    const result = await adminNutritionLibraryV2ControllerCreateSavedMeal({
        body: {
            name,
            timeFrom: meal.timeFrom,
            timeTo: meal.timeTo,
            items: meal.items as unknown as SavedMealV2Dto["items"],
        },
    });
    return toSavedMeal(unwrap<SavedMealV2Dto>(result, "saveMealToLibrary"));
};

/**
 * Rename only. `items` is deliberately omitted rather than sent as `[]` — the server reads an
 * absent list as "leave the contents alone", and an empty one as "empty the meal".
 */
export const renameSavedMeal = async (id: string, name: string): Promise<SavedMealV2> => {
    const result = await adminNutritionLibraryV2ControllerUpdateSavedMeal({
        path: { id: asId(id) },
        body: { name },
    });
    return toSavedMeal(unwrap<SavedMealV2Dto>(result, "renameSavedMeal"));
};

/**
 * Save a whole saved meal — name, suggested time and contents.
 *
 * A full replacement, like the menu and template saves. `renameSavedMeal` stays as the narrow
 * inline-rename path the list uses: it sends only the name, which the server reads as "leave the
 * items alone", so a rename can never empty a meal by omission.
 */
export const saveSavedMeal = async (
    meal: Pick<SavedMealV2, "id" | "name" | "timeFrom" | "timeTo" | "items">
): Promise<SavedMealV2> => {
    const body = {
        name: meal.name,
        timeFrom: meal.timeFrom,
        timeTo: meal.timeTo,
        items: meal.items as unknown as SavedMealV2Dto["items"],
    };
    // No id yet means it has never been written — the editor opens on "new" as well as on a row.
    const result = meal.id
        ? await adminNutritionLibraryV2ControllerUpdateSavedMeal({ path: { id: asId(meal.id) }, body })
        : await adminNutritionLibraryV2ControllerCreateSavedMeal({ body });
    return toSavedMeal(unwrap<SavedMealV2Dto>(result, "saveSavedMeal"));
};

export const deleteSavedMeal = async (id: string): Promise<void> => {
    const result = await adminNutritionLibraryV2ControllerDeleteSavedMeal({ path: { id: asId(id) } });
    if (result.error) {
        throw result.error;
    }
};

// ── company menu templates ──────────────────────────────────────────────────

const toTemplate = (dto: MenuTemplateV2Dto): MenuTemplateV2 => dto as unknown as MenuTemplateV2;

export const listMenuTemplates = async (): Promise<MenuTemplateV2[]> => {
    const result = await adminNutritionLibraryV2ControllerListTemplates();
    return unwrap<MenuTemplateV2Dto[]>(result, "listMenuTemplates").map(toTemplate);
};

export const saveMenuAsTemplate = async (
    name: string,
    menu: Pick<MenuV2, "meals">
): Promise<MenuTemplateV2> => {
    const result = await adminNutritionLibraryV2ControllerCreateTemplate({
        body: { name, meals: menu.meals as unknown as MenuTemplateV2Dto["meals"] },
    });
    return toTemplate(unwrap<MenuTemplateV2Dto>(result, "saveMenuAsTemplate"));
};

/** One template, with its meals — what the template builder opens. */
export const getMenuTemplate = async (id: string): Promise<MenuTemplateV2> => {
    const result = await adminNutritionLibraryV2ControllerGetTemplate({ path: { id: asId(id) } });
    return toTemplate(unwrap<MenuTemplateV2Dto>(result, "getMenuTemplate"));
};

/**
 * Save the whole template. A full replacement, the same as the menu save — the template builder IS
 * the menu builder, so its save has to mean the same thing.
 */
export const saveMenuTemplate = async (
    template: Pick<MenuTemplateV2, "id" | "name" | "meals" | "targets" | "qualityProteinPct" | "calculator">
): Promise<MenuTemplateV2> => {
    const body = {
        name: template.name,
        targets: template.targets ?? undefined,
        qualityProteinPct: template.qualityProteinPct ?? undefined,
        // Opaque both ways — the server stores and returns it as-is, so the client's calculator
        // shape is the only definition of it. Cast for the same reason `toSaveRequest` does.
        calculator: (template.calculator ?? undefined) as MenuTemplateV2Request["calculator"],
        meals: template.meals as unknown as MenuTemplateV2Dto["meals"],
    };
    // No id yet means this template has never been written — the builder can open on "new".
    const result = template.id
        ? await adminNutritionLibraryV2ControllerUpdateTemplate({ path: { id: asId(template.id) }, body })
        : await adminNutritionLibraryV2ControllerCreateTemplate({ body });
    return toTemplate(unwrap<MenuTemplateV2Dto>(result, "saveMenuTemplate"));
};

export const deleteMenuTemplate = async (id: string): Promise<void> => {
    const result = await adminNutritionLibraryV2ControllerDeleteTemplate({ path: { id: asId(id) } });
    if (result.error) {
        throw result.error;
    }
};

// ── company foods ───────────────────────────────────────────────────────────
//
// These used to be their own tables and their own /admin/nutrition-v2/company-foods endpoints.
// They are gone: `food_dictionary` carries `company_id`, so a company's own food is the same kind
// of row as a public one and lives in the same catalog. That removes a whole parallel food model
// — and, more usefully, it means a food a company adds is searchable through the ordinary food
// search rather than only through a second, company-only one.
//
// The functions keep their names and shapes so the library screen, the create-food dialog and
// their tests are unchanged. `ownOnly` is what makes them "the company's own": the catalog search
// is already scoped to the caller's company, and this narrows it further to the rows the company
// itself created, hiding the shared system catalog.

/** A catalog food in the shape the V2 screens read. */
const toCompanyFood = (food: CatalogFood): CompanyFoodV2 => ({
    id: String(food.id),
    name: food.name ?? "",
    brand: food.brand || undefined,
    imageUrl: food.imageUrl || undefined,
    barcode: food.barcode || undefined,
    servings: toServingOptions(food),
    createdDate: food.createdDate ?? new Date().toISOString(),
});

/**
 * The company's own foods. An empty keyword returns the whole list, which is what the library
 * shows before anything is typed.
 */
export const searchCompanyFoods = async (keyword: string): Promise<CompanyFoodV2[]> => {
    const res = await searchFoods({
        ...(keyword?.trim() ? { keyword: keyword.trim() } : {}),
        ownOnly: true,
        size: 100,
    });
    return (res?.data?.content ?? []).map(toCompanyFood);
};

export const listCompanyFoods = async (): Promise<CompanyFoodV2[]> => searchCompanyFoods("");

/**
 * Create a food owned by the caller's company.
 *
 * Every serving goes in the one create call, so a food is never left half-built. The first is the
 * default, which is what the create-food form implies by listing it first.
 *
 * Fiber travels as a FIBER micronutrient: the catalog has no fiber column, and the serving's
 * `nutritionValues.fiber` is ignored on the way in. It is sent only when the trainer typed a
 * positive amount — the form turns a blank into 0, and a stored 0 would claim a measurement nobody
 * made.
 */
export const createCompanyFood = async (draft: CompanyFoodDraft): Promise<CompanyFoodV2> => {
    const servings = draft.servings.map((serving, index) => ({
        servingName: serving.servingName?.trim() || "מנה",
        gramsPerServing: serving.gramsPerServing,
        isDefault: index === 0,
        nutritionValues: serving.nutrition,
        micronutrients: serving.nutrition?.fiber > 0 ? [{ code: "FIBER", amount: serving.nutrition.fiber }] : [],
    }));
    const created = await createFood({
        name: draft.name.trim(),
        brand: draft.brand?.trim() || undefined,
        barcode: draft.barcode?.trim() || undefined,
        servings,
    });
    if (!created?.success || !created?.data?.id) {
        throw new Error(created?.message || "שגיאה ביצירת המזון");
    }
    return toCompanyFood(created.data);
};

export const deleteCompanyFood = async (id: string): Promise<void> => {
    const result = await deleteFood(Number(id));
    if (!result?.success) {
        throw new Error(result?.message || "שגיאה במחיקת המזון");
    }
};
